if (!customElements.get('marame-size-picker')) {
  customElements.define('marame-size-picker', class MarameSizePicker extends HTMLElement {
    connectedCallback() {
      this.productInfo = this.closest('product-info');
      this.button = this.querySelector('[data-size-panel-open]');
      this.mainForm = this.productInfo?.querySelector('product-form form');
      this.mainButton = this.mainForm?.querySelector('[name="add"]');
      if (!this.button || !this.mainForm || !document.querySelector('marame-quick-add dialog')?.showModal) return;
      this.events?.abort();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.nativeLabel = this.mainButton.querySelector('span')?.textContent || '';
      this.button.hidden = false;
      this.dataset.enhanced = 'true';
      this.addEventListener('change', event => {
        const input = event.target.tagName === 'SELECT' ? event.target.selectedOptions[0] : event.target;
        if (input?.dataset.optionName === this.dataset.sizeOption) this.confirmedSize = input.value;
        if (this.dataset.lengthOption && input?.dataset.optionName === this.dataset.lengthOption) this.confirmedLength = input.value;
        this.sync();
      }, options);
      this.productInfo.addEventListener('submit', event => {
        if (event.target !== this.mainForm || this.hasConfirmedSize()) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        this.openPanel();
      }, { ...options, capture: true });
      this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (String(data.sectionId) !== this.dataset.section) return;
        this.nativeLabel = this.mainButton.querySelector('span')?.textContent || this.nativeLabel;
        this.sync();
      });
      this.sync();
    }

    disconnectedCallback() {
      this.events?.abort();
      this.unsubscribe?.();
      this.cancelSelection();
    }

    currentVariant() {
      try { return JSON.parse(this.querySelector('[data-selected-variant]')?.textContent || 'null'); }
      catch { return null; }
    }

    hasConfirmedSize() {
      const variant = this.currentVariant();
      const lengthIndex = Number(this.dataset.lengthIndex);
      const needsLength = Number.isInteger(lengthIndex) && lengthIndex >= 0;
      return Boolean(variant && this.confirmedSize && variant.options[Number(this.dataset.sizeIndex)] === this.confirmedSize && (!needsLength || (this.confirmedLength && variant.options[lengthIndex] === this.confirmedLength)));
    }

    sync() {
      if (!this.mainButton) return;
      const variant = this.currentVariant();
      const url = new URL(this.productInfo.dataset.url, location.href);
      if (variant?.id) url.searchParams.set('variant', variant.id);
      this.button.href = url.href;
      const lengthIndex = Number(this.dataset.lengthIndex);
      const length = Number.isInteger(lengthIndex) && lengthIndex >= 0 ? variant?.options[lengthIndex] : null;
      const selectedLabel = this.displaySize(this.confirmedSize) + (length ? ` / ${length}` : '');
      const label = this.hasConfirmedSize() ? this.dataset.selectedLabel.replace('[size]', selectedLabel) : this.dataset.chooseLabel;
      this.button.querySelector('span').textContent = label;
      const nativeText = this.mainButton.querySelector('span');
      if (nativeText && !this.mainButton.disabled && !this.mainButton.classList.contains('loading')) {
        nativeText.textContent = this.hasConfirmedSize() ? this.nativeLabel : this.dataset.chooseLabel;
      }
    }

    displaySize(size) {
      const input = [...this.querySelectorAll('variant-selects input[type="radio"], variant-selects option')].find(option => option.dataset.optionName === this.dataset.sizeOption && option.value === size);
      return input?.dataset.sizeDisplay || size;
    }

    openPanel() {
      if (this.dataset.enhanced !== 'true' || this.mainButton.classList.contains('loading')) return false;
      this.sync();
      this.button.click();
      return true;
    }

    context() {
      const variant = this.currentVariant();
      const formId = this.mainForm.querySelector('[name="id"]')?.value;
      if (!variant || this.productInfo.pendingRequestUrl || String(variant.id) !== String(formId)) return null;
      return { variant, sizeIndex: Number(this.dataset.sizeIndex), quantity: this.productInfo.querySelector('.quantity__input')?.value || '1' };
    }

    cancelSelection() {
      this.pendingSelection?.(false);
      this.pendingSelection = null;
    }

    selectSize(size, variantId, targetOptions) {
      this.cancelSelection();
      const input = [...this.querySelectorAll('variant-selects input[type="radio"], variant-selects option')].find(option => option.dataset.optionName === this.dataset.sizeOption && option.value === size);
      if (!input) return Promise.resolve(false);
      const current = this.currentVariant();
      let lengthInput;
      let lengthSelect;
      let lengthPicker;
      if (targetOptions !== undefined) {
        const sizeIndex = Number(this.dataset.sizeIndex);
        const lengthIndex = Number(this.dataset.lengthIndex);
        if (!Array.isArray(targetOptions) || !current?.options || targetOptions.length !== current.options.length || !Number.isInteger(sizeIndex) || sizeIndex < 0 || sizeIndex >= targetOptions.length || !Number.isInteger(lengthIndex) || lengthIndex < 0 || lengthIndex === sizeIndex || lengthIndex >= targetOptions.length || targetOptions[sizeIndex] !== size || targetOptions.some((value, index) => typeof value !== 'string' || (index !== sizeIndex && index !== lengthIndex && value !== current.options[index]))) return Promise.resolve(false);
        lengthInput = [...this.querySelectorAll('variant-selects input[type="radio"], variant-selects option')].find(option => option.dataset.optionName === this.dataset.lengthOption && option.value === targetOptions[lengthIndex]);
        if (!lengthInput || (String(current.id) === String(variantId) && targetOptions.some((value, index) => value !== current.options[index]))) return Promise.resolve(false);
        if (lengthInput.tagName === 'OPTION') {
          lengthSelect = lengthInput.closest('select');
          lengthPicker = lengthInput.closest('variant-selects');
          if (!lengthSelect || typeof lengthPicker?.updateSelectionMetadata !== 'function') return Promise.resolve(false);
        }
      }
      this.confirmedSize = size;
      if (Number(this.dataset.lengthIndex) >= 0) this.confirmedLength = lengthInput?.value || null;
      if (String(current?.id) === String(variantId) && !this.mainButton.disabled) {
        this.productInfo.dispatchEvent(new CustomEvent('marame:size-confirmed', { bubbles: true, detail: { name: this.dataset.sizeOption, value: size } }));
        this.sync();
        return Promise.resolve(true);
      }
      return new Promise(resolve => {
        let settled = false;
        const finish = result => {
          if (settled) return;
          settled = true;
          clearTimeout(timer);
          unsubscribe();
          if (this.pendingSelection === finish) this.pendingSelection = null;
          resolve(result);
        };
        const unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data.sectionId) === this.dataset.section) finish(String(data.variant?.id) === String(variantId));
        });
        const timer = setTimeout(() => finish(false), 10000);
        this.pendingSelection = finish;
        if (lengthInput) {
          if (lengthSelect) {
            lengthSelect.value = lengthInput.value;
            // Dawn reads option[selected] metadata, not the select's live value.
            // Update both axes before the single Size change starts its request.
            lengthPicker.updateSelectionMetadata({ target: lengthSelect });
          } else lengthInput.checked = true;
        }
        if (input.tagName === 'OPTION') {
          const select = input.closest('select');
          select.value = size;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        } else {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }

    quantityRules(choice) {
      const input = this.productInfo.querySelector('.quantity__input');
      const min = Number(input?.dataset.min || choice.dataset.quantity || 1);
      const step = Number(input?.step || choice.dataset.quantityStep || 1);
      const cart = Number(input?.dataset.cartQuantity || 0);
      const rawMax = input?.dataset.max ?? choice.dataset.quantityMax;
      const max = rawMax === undefined || rawMax === '' ? null : Number(rawMax) - cart;
      return { min: cart >= min ? Math.min(min, step) : min, step, max };
    }

    validQuantity(value, rules) {
      const number = Number(value);
      return Number.isInteger(number) && number >= rules.min && (rules.max === null || number <= rules.max) && (number - rules.min) % rules.step === 0;
    }

    setQuantity(value) {
      const input = this.productInfo.querySelector('.quantity__input');
      if (input) input.value = String(value);
    }

    openGuide(trigger) {
      const guide = this.productInfo.querySelector('marame-size-guide');
      if (!guide?.openGuide) return false;
      guide.openGuide();
      // Wait for the sticky control's overlay observer to make it focusable again.
      guide.dialog.addEventListener('close', () => requestAnimationFrame(() => requestAnimationFrame(() => {
        if (guide.dialog.open || document.querySelector('dialog[open]')) return;
        const active = document.activeElement;
        if (active && active !== document.body && !guide.dialog.contains(active)) return;
        const candidates = [trigger, this.button, document.querySelector(`marame-sticky-purchase[data-section="${this.dataset.section}"] [data-sticky-submit]`)];
        const returnTo = candidates.find(element => element?.isConnected && !element.closest('dialog:not([open]), [inert]') && element.getClientRects?.().length);
        returnTo?.focus({ preventScroll: true });
      })), { once: true });
      return true;
    }
  });
}

